import { useMemo } from 'react';
import {
  QueryClient,
  QueryFunctionContext,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import useCredentials from '@/hooks/useCredentials';
import useErrorBoundary from '@/hooks/useErrorBoundary';
import {isInvalidTokenException, shouldRetry} from '@/domains/auth.usecase';

interface UseQueryProxyOptions<T> extends Omit<UseQueryOptions<T>, 'queryFn'> {
  queryFn: (accessToken: string, context: QueryFunctionContext) => Promise<T>;
}

const useQueryProxy = <T,>(options: UseQueryProxyOptions<T>, queryClient?: QueryClient) => {
  const { getAccessTokenSilently } = useCredentials();
  const { showBoundary } = useErrorBoundary();

  const optionsWithAuth = useMemo(() => {
    return {
      ...options,
      queryFn: async (context: QueryFunctionContext) => {
        const token = await getAccessTokenSilently();
        return options.queryFn(token, context);
      },
      retry: (failureCount: number, error: Error) => {
        const isRetryEnabled = shouldRetry(failureCount, error);
        if (!isRetryEnabled && isInvalidTokenException(error)) {
          showBoundary(error);
        }
        return isRetryEnabled;
      },
    };
  }, [options, getAccessTokenSilently, showBoundary]);

  return useQuery(optionsWithAuth, queryClient);
};

export default useQueryProxy;
